import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { 
  FiPackage, FiSearch, FiX, FiChevronDown, FiChevronUp, FiClock
} from "react-icons/fi";
import Select from 'react-select';
import { MdOutlineAssignmentReturn } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import axios from 'axios';
import { BASE_URL, GET_INVOICE_DETAILS, POST_SALES_RETURN, GET_PATIENTS, GET_SALES_REPORT, GET_OP_DETAILS } from '../utlilities/config';
import ReturnPrint from '../components/ReturnPrint';
import { toPng } from 'html-to-image';
import KotReturnBillPrint from '../components/KotReturnBill';
import { TabContext } from '../components/TabbedLayout';

// Enhanced Spinner Component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '34px', // reduce overall control height
    height: '34px',    // explicitly set height
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px', // reduce internal padding for smaller height
    height: '34px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '34px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    padding: '6px 8px',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};


const PharmacySalesReturn = () => {
  const tabContext = useContext(TabContext);
  const activeTabId = tabContext?.activeTabId;
  const token = localStorage.getItem('accessToken');
  const [loading, setLoading] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [header, setHeader] = useState(null); // Store header data from API
  const [billedDate, setBilledDate] = useState('');
  const [printAfterSave, setPrintAfterSave] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [searchMode, setSearchMode] = useState('invoice'); // 'invoice' or 'op'
  const [isPrev, setIsPrev] = useState(false);
  const [opNumber, setOpNumber] = useState('');
  const loadedOpnumRef = useRef('');
  
  // Print component state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const normalBillPrintRef = useRef(null);
  const [sysInfo, setSysInfo] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [generalsettings, setGeneralSettings] = useState({});
  
  // Patient selection and history states
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientHistory, setPatientHistory] = useState([]);
  const [expandedBillId, setExpandedBillId] = useState(null);
  const [showPatientSidebar, setShowPatientSidebar] = useState(false);
  
  // Refs
  const invoiceNumberRef = useRef(null);
  const returnQtyRefs = useRef([]);
  const returnReasonRef = useRef(null);
  const submitButtonRef = useRef(null);
  const mobileInputRef = useRef(null);
  const patientSearchDebounceRef = useRef(null);

  // Helper function to parse decimal values (3 decimal places)
  const parseDecimal = (value) => parseFloat(parseFloat(value || 0).toFixed(3));
  
  // Helper function to format numbers for display (3 decimal places)
  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseDecimal(value).toFixed(3);
  };


  const returnReasons = [
    { value: 'expired', label: 'Expired Medicine' },
    { value: 'damaged', label: 'Damaged Product' },
    { value: 'wrong_medicine', label: 'Wrong Medicine Dispensed' },
    { value: 'patient_request', label: 'Patient Request' },
    { value: 'doctor_change', label: 'Doctor Changed Prescription' },
    { value: 'side_effects', label: 'Side Effects' },
    { value: 'other', label: 'Other' }
  ];

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;

  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  useEffect(() => {
    const fetchData = async () => {
      let sysinfo;
      let generalSetting;
      let userinfo;

      if(isElectron() && ipcRenderer){
        userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
        sysinfo = await ipcRenderer.invoke('store-get', 'sysInfo');
        generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
      } 
      setSysInfo(sysinfo || {});
      setGeneralSettings(generalSetting || {});
      setUserInfo(userinfo || {});

      if(generalSetting?.phAutoBillPrint){
        setPrintAfterSave(true)
      } else  {
        setPrintAfterSave(false)
      }

      console.log('generalSetting', generalSetting)

    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus invoice number field on mount and when tab becomes active
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      invoiceNumberRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTabId]); // Re-run when activeTabId changes (tab switch)

  // Set current billed date on mount only
  useEffect(() => {
    // Set current billed date
    const billigDate = new Date();
    const newdate = formatBilledDate(billigDate);
    setBilledDate(newdate);

    // Cleanup debounce timeout on unmount
    return () => {
      if (patientSearchDebounceRef.current) {
        clearTimeout(patientSearchDebounceRef.current);
      }
    };
  }, []);

  // Fetch patient history (all bills)
  const fetchPatientHistory = useCallback(async (patientId) => {
    setLoading(true);
    try {
      // Convert date strings to Date objects and format as YYYY-MM-DD
      const fromDate = new Date('2020-01-01');
      const toDate = new Date();
      
      // Format date as YYYY-MM-DD
      const formatDate = (date) => {
          if (!date) return '';
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
      };

      const response = await axios.get(`${BASE_URL}${GET_SALES_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          'rpType': 'Patient',
          'fromDate': formatDate(fromDate),
          'toDate': formatDate(toDate),
          'searchKey': patientId,
          'patientId': 0,
          'itemNo': 0,
        },
      });
      if (response.status === 200) {
        setLoading(false);
        const data = (response.data.data) || [];
        console.log('Sales report:', data);
        // Sort by InvoiceNo in descending order (latest first)
        const sortedData = [...data].sort((a, b) => {
          const invoiceNoA = parseInt(a.InvoiceNo || 0);
          const invoiceNoB = parseInt(b.InvoiceNo || 0);
          return invoiceNoB - invoiceNoA; // Descending order
        });
        setPatientHistory(sortedData);
      } else {
        setLoading(false);
        toast.error('Failed to fetch sales report', { autoClose: 1000 });
      }
    } catch (error) {
      setLoading(false);
      console.error('Failed to fetch sales report:', error);
      toast.error('Failed to fetch sales report', { autoClose: 1000 });
    }
  }, [token]);

  // Fetch patient history when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      fetchPatientHistory(selectedPatient.PatientID);
    } else {
      setPatientHistory([]);
    }
  }, [selectedPatient, fetchPatientHistory]);

  // Format date for display
  const formatBilledDate = (date) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
  
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
  
    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  // Get patient data for search
  const getPatientData = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) {
      setPatients([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_PATIENTS}`, {
        headers: {
          'PageNo': 1,
          'PageSize': 100,
          'FilterText': inputValue.trim(),
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200 && response.data && response.data.data) {
        const patientData = response.data.data.masterData || [];
        setPatients(patientData);
      } else {
        setPatients([]);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Failed to filter patients.', {
        autoClose: 1000,
      });
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle mobile/name input change
  const handleMobileChange = async (inputValue) => {
    const value = inputValue || '';

    if (!value || value === null || value === undefined || value.trim().length === 0) {
      // Clear patients when input is cleared
      setPatients([]);
      return;
    }

    // Clear previous debounce timeout
    if (patientSearchDebounceRef.current) {
      clearTimeout(patientSearchDebounceRef.current);
    }

    // Call API when user types 2+ characters with debounce (similar to medicine search)
    if (value && value.length >= 2) {
      patientSearchDebounceRef.current = setTimeout(() => {
        getPatientData(value);
      }, 300); // 300ms debounce
    } else {
      // Clear patients when input is too short
      setPatients([]);
    }
  };

  // Handle patient selection from dropdown
  const handleSuggestionClick = (selectedOption) => {
    if (selectedOption) {
      setSelectedPatient(selectedOption);
    } else {
      setSelectedPatient(null);
    }
  };


  // Handle bill selection for return
  const handleBillSelect = async (invoiceNo) => {
    setShowPatientSidebar(false);
    setInvoiceNumber(invoiceNo.toString());
    const invoice = await loadInvoiceDetails(invoiceNo.toString());
    
    if (invoice) {
      setInvoiceData(invoice);
      
      // Initialize return items with 0 quantity
      const initialReturnItems = invoice.items.map(item => ({
        ...item,
        returnQty: parseDecimal(0),
        returnAmount: parseDecimal(0),
        returnDiscountAmount: parseDecimal(0),
        returnSGST: parseDecimal(0),
        returnCGST: parseDecimal(0),
        returnTotal: parseDecimal(0),
        availableQty: parseDecimal(item.qty - item.returnedQty)
      }));
      
      setReturnItems(initialReturnItems);
      
      // Focus on first return quantity input after loading
      setTimeout(() => {
        if (returnQtyRefs.current[0]) {
          returnQtyRefs.current[0].focus();
        }
      }, 100);
    } else {
      setInvoiceData(null);
    }
  };

  // Custom option label for patient dropdown
  const customOptionLabel = ({ PatientName, Age, Month, Days, MobileNo, PatientID }) => {
    // Convert to numbers and check if greater than zero
    const ageNum = parseInt(Age) || 0;
    const monthNum = parseInt(Month) || 0;
    const daysNum = parseInt(Days) || 0;
    
    return (
      <div className="flex items-center space-x-3 py-1">
        <div className="flex flex-col flex-1">
          {PatientName && (
            <div className="font-semibold text-xs text-black">
              {PatientName}
            </div>
          )}
          {MobileNo && (
            <div className="text-xs text-black mt-0.5">
              {MobileNo}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {ageNum > 0 && (
            <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
              {Age} Y
            </div>
          )}
          {monthNum > 0 && (
            <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
              {Month} M
            </div>
          )}
          {daysNum > 0 && (
            <div className="text-xs px-2 py-0.5 bg-indigo-50 text-black rounded-full">
              {Days} D
            </div>
          )}
        </div>
      </div>
    );
  };

  // Prepare options for patient dropdown
  const options = patients.map(patient => ({
    value: patient.PatientID,
    label: patient.PatientName,
    ...patient
  }));

  // Load invoice details from API
  const loadInvoiceDetails = async (invoiceNo) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          Sequence: isPrev ? '2025' : localStorage.getItem('finac_year'),
          InvoiceNo: invoiceNo.toString(),
        }
      });

      if (response.status === 200) {
        const invoiceData = response.data.data;
        
        if (!invoiceData || !invoiceData.header || !invoiceData.details) {
          toast.error('Invalid invoice data received.', { autoClose: 1000 });
          setLoading(false);
          return null;
        }

        const { header, details, itemList } = invoiceData;
        
        // Validate return validity period
        if (header.EntryDate && generalsettings?.phReturnValidityDays) {
          const entryDate = new Date(header.EntryDate);
          const today = new Date();
          // Reset time to compare only dates
          entryDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          const diffTime = today - entryDate;
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays > generalsettings.phReturnValidityDays) {
            toast.error(`Return validity expired! Invoice is ${diffDays} days old. Maximum allowed is ${generalsettings.phReturnValidityDays} days.`, { autoClose: 3000 });
            setLoading(false);
            return null;
          }
        }
        
        // Store header data for use in return submission
        setHeader(header);

        // Create a map of itemList for quick lookup
        const itemListMap = {};
        if (itemList && Array.isArray(itemList)) {
          itemList.forEach(item => {
            itemListMap[item.Item_No] = item;
          });
        }

        // Format invoice date
        let invoiceDate = '';
        if (header.EntryDate) {
          const entryDate = new Date(header.EntryDate);
          if (!isNaN(entryDate.getTime())) {
            invoiceDate = formatBilledDate(entryDate);
            setBilledDate(invoiceDate);
          }
        }

        // Transform details into items format for return
        const transformedItems = details.map((detail, index) => {
          const itemInfo = itemListMap[detail.ItemNo] || {};
          
          // Extract expiry date from ItemBatch JSON string
          let expiryDate = '';
          if (itemInfo.ItemBatch) {
            try {
              const itemBatches = JSON.parse(itemInfo.ItemBatch);
              if (Array.isArray(itemBatches)) {
                const matchingBatch = itemBatches.find(batch => batch.BatchCode === detail.BatchCode);
                if (matchingBatch && matchingBatch.ExpiryDate) {
                  // Format expiry date from YYYY-MM-DD to YYYY-MM
                  const expiryDateObj = new Date(matchingBatch.ExpiryDate);
                  if (!isNaN(expiryDateObj.getTime())) {
                    const year = expiryDateObj.getFullYear();
                    const month = (expiryDateObj.getMonth() + 1).toString().padStart(2, '0');
                    expiryDate = `${year}-${month}`;
                  }
                }
              }
            } catch (error) {
              console.warn('Failed to parse ItemBatch for item:', detail.ItemNo, error);
            }
          }
          
          // Calculate discount percentage from detail
          const discountPercentage = parseDecimal(detail.Disc_Percentage || 0);
          const discountAmount = parseDecimal(detail.Disc_Amount || 0);
          
          // PostTaxTotal already includes discount and tax - this is what customer paid
          const postTaxTotal = parseDecimal(detail.PostTaxTotal || 0);
          
          // Calculate GST amounts for display (split equally between SGST and CGST)
          const taxAmount = parseDecimal(detail.TaxAmount || 0);
          const sgst = parseDecimal(taxAmount / 2); // Split GST equally
          const cgst = parseDecimal(taxAmount / 2);

          // TODO: Fetch already returned quantities from API
          // For now, assuming no items have been returned
          const returnedQty = parseDecimal(0); // This should come from API in future

          return {
            id: detail.ItemNo,
            name: detail.ItemName || itemInfo.Item_Name || '',
            batchNo: detail.BatchCode || '',
            expiryDate: expiryDate,
            qty: parseDecimal(detail.Qty || 0),
            rate: parseDecimal(detail.SalesPrice || detail.MRP || 0),
            mrp: parseDecimal(detail.MRP || detail.SalesPrice || 0),
            purchasePrice: parseDecimal(detail.PurchasePrice || 0), // Store purchase price for profit calculation
            amount: parseDecimal(detail.Total || 0), // Original amount before discount
            discount: discountPercentage > 0 ? `${discountPercentage}%` : '',
            discountAmount: discountAmount,
            sgst: sgst,
            cgst: cgst,
            totalAmount: postTaxTotal, // PostTaxTotal (includes discount and tax)
            postTaxTotal: postTaxTotal, // Store PostTaxTotal separately for return calculations
            returnedQty: returnedQty, // Already returned quantity
            originalQty: parseDecimal(detail.OrginalQty || 0),
            gstRate: parseDecimal(detail.TaxCode || 0) // GST rate for calculations
          };
        });

        // Build invoice data object
        const invoice = {
          invoiceNumber: header.InvoiceNo?.toString() || invoiceNo.toString(),
          invoiceDate: invoiceDate,
          patient: {
            name: header.PatientName || '',
            age: '', // May need to fetch from patient API
            gender: '', // May need to fetch from patient API
            mobile: header.MobileNo || '', // May need to fetch from patient API
            place: '' // May need to fetch from patient API
          },
          doctor: header.DoctorName || '',
          items: transformedItems,
          totalAmount: parseDecimal(header.TotalAmount || 0), // Total before discount
          subtotal: parseDecimal(header.NetAmount || 0), // Net amount after discount
          totalTax: parseDecimal(header.TaxAmount || 0), // Total GST
          totalSGST: parseDecimal(parseDecimal(header.TaxAmount || 0) / 2),
          totalCGST: parseDecimal(parseDecimal(header.TaxAmount || 0) / 2),
          discountPercentage: parseDecimal(header.Discount_Percentage || 0),
          discountAmount: parseDecimal(header.DiscountAmount || 0),
          grandTotal: parseDecimal(parseDecimal(header.NetAmount || 0) + parseDecimal(header.TaxAmount || 0))
        };

        return invoice;
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to load invoice details.', { autoClose: 2000 });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle OP number search
  const handleOpSearch = async ({ focusNext = true } = {}) => {
    const opToLoad = (opNumber || '').trim();
    if (!opToLoad) {
      loadedOpnumRef.current = '';
      if (focusNext) {
        toast.error('Please enter an OP number', { autoClose: 1000 });
      }
      return { ok: false };
    }

    if (opToLoad === String(loadedOpnumRef.current).trim() && selectedPatient) {
      return { ok: true, alreadyLoaded: true };
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_OP_DETAILS}`, {
        headers: {
          'Sequence': 0,
          'InvoiceNo': opToLoad,
          'EditNo': 0,
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200 && response.data.data && response.data.data.length > 0) {
        const patientData = response.data.data[0];
        loadedOpnumRef.current = opToLoad;
        
        // Set selected patient
        setSelectedPatient({
          PatientID: patientData.PatientID,
          PatientName: patientData.PatientName,
          Age: patientData.Age,
          Place: patientData.Place,
          MobileNo: patientData.MobileNo,
          DOB: patientData.DOB,
          Month: patientData.Month,
          Days: patientData.Days,
          Gender: patientData.Gender
        });

        // Fetch patient bills using the patient ID
        await fetchPatientHistory(patientData.PatientID);
        
        // Show patient sidebar with bills
        setShowPatientSidebar(true);
        
        toast.success('Patient data loaded. Please select a bill from the sidebar.', { autoClose: 2000 });
        return { ok: true };
      } else {
        toast.error('No patient found for this OP number', { autoClose: 2000 });
        return { ok: false };
      }
    } catch (error) {
      console.error('Error fetching OP details:', error);
      toast.error('Failed to fetch patient data from OP number.', { autoClose: 2000 });
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  const handleOpnumBlur = () => {
    if (searchMode !== 'op') return;
    const current = (opNumber || '').trim();
    if (!current) {
      loadedOpnumRef.current = '';
      return;
    }
    if (current === String(loadedOpnumRef.current).trim() && selectedPatient) {
      return;
    }
    handleOpSearch({ focusNext: false });
  };

  const handleInvoiceSearch = async () => {
    // Handle based on search mode
    if (searchMode === 'op') {
      await handleOpSearch();
      return;
    }

    if (!invoiceNumber.trim()) {
      toast.error('Please enter an invoice number', { autoClose: 1000 });
      return;
    }

    const invoice = await loadInvoiceDetails(invoiceNumber.trim());
    console.log(invoice);
    
    if (invoice) {
      setInvoiceData(invoice);
      
      // Initialize return items with 0 quantity
      const initialReturnItems = invoice.items.map(item => ({
        ...item,
        returnQty: parseDecimal(0),
        returnAmount: parseDecimal(0),
        returnDiscountAmount: parseDecimal(0),
        returnSGST: parseDecimal(0),
        returnCGST: parseDecimal(0),
        returnTotal: parseDecimal(0),
        availableQty: parseDecimal(item.qty - item.returnedQty)
      }));
      
      setReturnItems(initialReturnItems);
      
      // Focus on first return quantity input after loading
      setTimeout(() => {
        if (returnQtyRefs.current[0]) {
          returnQtyRefs.current[0].focus();
        }
      }, 100);
    } else {
      setInvoiceData(null);
    }
  };

  const handleReturnQtyChange = (index, newQty) => {
    const updatedReturnItems = returnItems.map((item, i) => {
      if (i === index) {
        const qty = parseDecimal(newQty || 0);
        console.log('hereeee',item)
        console.log('qttttt1', newQty)
        
        // Validate quantity
        if (qty > item.availableQty) {
          toast.error(`Return quantity cannot exceed available quantity (${item.availableQty})`, { autoClose: 2000 });
          return item;
        }

        if (qty < 0) {
          toast.error('Return quantity cannot be negative', { autoClose: 2000 });
          return item;
        }

        // Calculate return amounts proportionally
        // PostTaxTotal already includes discount and tax - this is what customer paid
        const returnRatio = parseDecimal(qty / item.originalQty);
        const returnTotal = parseDecimal(parseDecimal(item.postTaxTotal || item.totalAmount) * returnRatio);
        
        // Extract tax portion from return total
        // PostTaxTotal = baseAmount + tax, where tax = baseAmount * (gstRate/100)
        // So: PostTaxTotal = baseAmount * (1 + gstRate/100)
        // Therefore: baseAmount = PostTaxTotal / (1 + gstRate/100)
        const gstRate = parseDecimal(item.gstRate || 5); // Default to 12% if not available
        const gstMultiplier = parseDecimal(1 + (gstRate / 100));
        const returnBaseAmount = parseDecimal(returnTotal / gstMultiplier);
        const returnTaxAmount = parseDecimal(returnTotal - returnBaseAmount);
        
        // Split tax equally between SGST and CGST
        const returnSGST = parseDecimal(returnTaxAmount / 2);
        const returnCGST = parseDecimal(returnTaxAmount / 2);
        
        // Calculate return amount before discount (for display)
        // Original amount proportionally
        const returnAmount = parseDecimal(parseDecimal(item.amount) * returnRatio);
        // Discount amount proportionally
        const returnDiscountAmount = parseDecimal(parseDecimal(item.discountAmount) * returnRatio);

        return {
          ...item,
          returnQty: qty,
          returnAmount: returnAmount,
          returnDiscountAmount: returnDiscountAmount,
          returnSGST: returnSGST,
          returnCGST: returnCGST,
          returnTotal: returnTotal
        };
      }
      return item;
    });

    setReturnItems(updatedReturnItems);
  };

  const handleReturnQtyKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextIndex = index + 1;
      if (nextIndex < returnItems.length && returnQtyRefs.current[nextIndex]) {
        returnQtyRefs.current[nextIndex].focus();
      } else {
        returnReasonRef.current?.focus();
      }
    }
  };

  const calculateTotalReturn = () => {
    return parseDecimal(returnItems.reduce((total, item) => total + parseDecimal(item.returnTotal || 0), 0));
  };

  const calculateTotalReturnSGST = () => {
    return parseDecimal(returnItems.reduce((total, item) => total + parseDecimal(item.returnSGST || 0), 0));
  };

  const calculateTotalReturnCGST = () => {
    return parseDecimal(returnItems.reduce((total, item) => total + parseDecimal(item.returnCGST || 0), 0));
  };

  // Calculate functions for print component
  const calculateTotalDiscount = () => {
    return parseDecimal(returnItems.reduce((total, item) => total + parseDecimal(item.returnDiscountAmount || 0), 0)).toFixed(2);
  };

  const calculateTotalGST = () => {
    return parseDecimal(returnItems.reduce((total, item) => total + parseDecimal(item.returnSGST || 0) + parseDecimal(item.returnCGST || 0), 0)).toFixed(2);
  };

  const calculateGrandTotal = () => {
    return parseDecimal(calculateTotalReturn()).toFixed(2);
  };

  const calculateTotal = () => {
    return calculateGrandTotal();
  };

  const handleSubmitReturn = async () => {
    setIsButtonDisabled(true);
    const itemsToReturn = returnItems.filter(item => item.returnQty > 0);
    
    if (itemsToReturn.length === 0) {
      toast.error('Please enter return quantities for at least one item', { autoClose: 2000 });
      setIsButtonDisabled(false);
      return;
    }

    // if (!returnReason) {
    //   toast.error('Please select a return reason', { autoClose: 2000 });
    //   setIsButtonDisabled(false);
    //   returnReasonRef.current?.focus();
    //   return;
    // }

    if (!header) {
      toast.error('Invoice header data not available. Please reload the invoice.', { autoClose: 2000 });
      setIsButtonDisabled(false);
      return;
    }

    // Validate return validity period at submission time
    if (header.EntryDate && generalsettings?.phReturnValidityDays) {
      const entryDate = new Date(header.EntryDate);
      const today = new Date();
      // Reset time to compare only dates
      entryDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      const diffTime = today - entryDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Reject if the difference is more than the allowed validity days
      // If phReturnValidityDays is 7, it accepts 0-7 days (inclusive of 7th day)
      if (diffDays > generalsettings.phReturnValidityDays) {
        toast.error(`Return validity expired! Invoice is ${diffDays} days old. Maximum allowed is ${generalsettings.phReturnValidityDays} days.`, { autoClose: 3000 });
        setIsButtonDisabled(false);
        return;
      }
    }

    setLoading(true);

    try {
      // Calculate totals from return items
      const totalAmountBeforeDiscount = parseDecimal(itemsToReturn.reduce((sum, item) => sum + parseDecimal(item.returnAmount || 0), 0));
      const totalDiscount = parseDecimal(itemsToReturn.reduce((sum, item) => sum + parseDecimal(item.returnDiscountAmount || 0), 0));
      const totalTax = parseDecimal(itemsToReturn.reduce((sum, item) => sum + parseDecimal(item.returnSGST || 0) + parseDecimal(item.returnCGST || 0), 0));
      const netAmount = parseDecimal(calculateTotalReturn()); // Net amount is the grand total (after discount, inclusive of GST)
      const totalAmount = parseDecimal(totalAmountBeforeDiscount); // Total before discount
      
      // Calculate total purchase cost (for profit calculation)
      let totalPurchaseCost = parseDecimal(0);
      
      // Build items array with profit calculations
      const items = itemsToReturn.map((item, index) => {
        const qty = parseDecimal(item.returnQty || 0);
        // Get purchase price from original item data - need to fetch from API or store it
        // For now, we'll need to get it from the detail or calculate it
        // Assuming we can get it from the original invoice detail
        const purchasePrice = parseDecimal(item.purchasePrice || 0); // This should be stored when loading invoice
        const mrp = parseDecimal(item.mrp || item.rate || 0);
        const salesPrice = mrp; // MRP is the sales price
        const discAmount = parseDecimal(item.returnDiscountAmount || 0);
        const discPercentage = parseDecimal(item.returnAmount > 0 ? (discAmount / parseDecimal(item.returnAmount || 1)) * 100 : 0);
        const taxAmount = parseDecimal(parseDecimal(item.returnSGST || 0) + parseDecimal(item.returnCGST || 0));
        const netTotal = parseDecimal(parseDecimal(item.returnTotal || 0) - taxAmount); // After discount, before GST
        const total = parseDecimal(item.returnAmount || 0); // Before discount
        const postTaxTotal = parseDecimal(item.returnTotal || 0); // Same as returnTotal (includes discount and tax)
        
        // Calculate profit for this item (negative for return)
        const purchaseCost = parseDecimal(purchasePrice * qty);
        const gpAmount = parseDecimal(netTotal - purchaseCost); // Profit = Sales (after discount) - Purchase cost (negative for return)
        const gpPercentage = parseDecimal(purchaseCost > 0 ? (gpAmount / purchaseCost) * 100 : 0);
        
        // Add to total purchase cost
        totalPurchaseCost = parseDecimal(totalPurchaseCost + purchaseCost);
        
        return {
          SI_No: index + 1,
          ItemNo: item.id || 0,
          ItemName: item.name || '',
          TaxCode: String(item.gstRate || 0),
          BatchCode: item.batchNo || '',
          Qty: qty,
          Foc: 0, // FOC quantity if applicable
          SalesPrice: salesPrice,
          PurchasePrice: purchasePrice,
          Mrp: mrp,
          Total: total-discAmount,
          Disc_Percentage: discPercentage,
          Disc_Amount: discAmount,
          TaxAmount: taxAmount,
          NetTotal: netTotal,
          PostTaxTotal: postTaxTotal,
          GP_Percentage: gpPercentage,
          GP_Amount: gpAmount,
          Onhand: parseDecimal(item.availableQty || 0),
          DepartmentID: localStorage.getItem('department_id') || ''
        };
      });

      // Calculate overall profit for the return (negative)
      const gpAmount = parseDecimal(netAmount - totalPurchaseCost); // Overall profit = Total sales (after discount) - Total purchase cost
      const gpPercentage = parseDecimal(totalPurchaseCost > 0 ? (gpAmount / totalPurchaseCost) * 100 : 0);
      const discountPercentage = parseDecimal(totalAmountBeforeDiscount > 0 ? (totalDiscount / totalAmountBeforeDiscount) * 100 : 0);

      // Get current date in local timezone (IST)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const entryDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD (in local timezone)

      // Build payload
      const payload = {
        PatientID: parseDecimal(header.PatientID || 0),
        OpNo: parseDecimal(header.OPNo || 0),
        RefDoctorID: parseDecimal(header.RefDoctorID || 0),
        EntryDate: entryDate,
        TotalAmount: parseDecimal(totalAmount)-parseDecimal(totalDiscount), // Total before discount
        NetAmount: parseDecimal(netAmount - totalTax), // Net amount after discount (before GST)
        TaxAmount: parseDecimal(totalTax),
        Discount_Percentage: discountPercentage,
        DiscountAmount: parseDecimal(totalDiscount),
        GpAmount: gpAmount,
        GP_Percentage: gpPercentage,
        CashAmount: 0,
        CardAmount: 0,
        BankAmount: 0,
        BalanceDue: parseDecimal(netAmount), // For return, balance due is the return amount
        PatientName: header.PatientName || '',
        MobileNo: header.MobileNo || '',
        Items: items
      };

      console.log('Sales Return Payload:', payload);

      const response = await axios.post(`${BASE_URL}${POST_SALES_RETURN}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'sequence': header.Sequence || 0,
          'invoiceNo': header.InvoiceNo || 0,
          'editNo': header.EditNo || 0,
          'docStatus': 'O',
        }
      });

      if (response.status === 200) {
        toast.success(`Return processed successfully! Total return amount: ₹${formatDecimal(calculateTotalReturn())}`, { autoClose: 2000 });

        if (printAfterSave) {
          handlePrint();
        }

        
        // Focus back to invoice input
        setTimeout(() => {
          setLoading(false);
          setInvoiceNumber('');
          setOpNumber('');
          setInvoiceData(null);
          setReturnItems([]);
          setReturnReason('');
          setHeader(null);
          setSelectedPatient(null);
          setPatientHistory([]);
          setShowPatientSidebar(false);
          invoiceNumberRef.current?.focus();
          setIsButtonDisabled(false);
        }, 1500);
      } else {
        throw new Error('Failed to save sales return');
      }
    } catch (error) {
      setLoading(false);
      setIsButtonDisabled(false);
      console.error('Error saving sales return:', error);
      toast.error('Failed to save sales return.', { autoClose: 2000 });
    }
  };

  const resetForm = () => {
    setInvoiceNumber('');
    setOpNumber('');
    setInvoiceData(null);
    setReturnItems([]);
    setReturnReason('');
    setHeader(null);
    setIsButtonDisabled(false);
    setSelectedPatient(null);
    setPatientHistory([]);
    setExpandedBillId(null);
    setShowPatientSidebar(false);
    
    // Set current billed date
    const billigDate = new Date();
    const newdate = formatBilledDate(billigDate);
    setBilledDate(newdate);
    
    invoiceNumberRef.current?.focus();
  };

  const printKOT = async () => {
    if (!isElectron()) {
      toast.error('Not Allowed', { duration: 1000 });
      return;
    }

    if(!sysInfo.pharmacyBillPriinter){
      toast.error('Cannot Print,Printer Not Selected ', { duration: 1000 });
      return;
    }

    const kotDiv = document.getElementById('kotDiv');
    
    if (!kotDiv) {
      toast.error('KOT element not found', { duration: 1000 });
      return;
    }

    try {
      // Function to convert image to base64
      const getBase64Image = (imgPath) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
          };
          img.onerror = reject;
          img.src = imgPath;
        });
      };

      // Convert the QR code image to base64
      let base64Image = '';
      try {
        base64Image = await getBase64Image(`${process.env.PUBLIC_URL}/general.png`);
      } catch (error) {
        console.warn('Failed to convert image to base64:', error);
      }

      // Clone the KOT div and replace the image source
      const kotClone = kotDiv.cloneNode(true);
      const imgElement = kotClone.querySelector('img');
      if (imgElement && base64Image) {
        imgElement.src = base64Image;
      }

      // Get the HTML content with base64 image
      const kotHTML = kotClone.outerHTML;
      
      // Enhanced CSS for printing with better image handling
      const printCSS = `
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Courier Prime', 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.2;
            color: #000;
            background: #fff;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          } 
          
          #kotDiv {
            width: 302px;
            margin: 0 auto;
            padding: 8px;
            background: #fff;
            color: #000;
            font-family: 'Courier Prime', 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.2;
          }
          
          .text-xs { font-size: 11px; }
          .text-sm { font-size: 13px; }
          .text-lg { font-size: 14px; }
          .font-bold { font-weight: 700; }
          .font-extrabold { font-weight: 800; }

          /* Fixed width classes for table alignment */
          .w-\\[70\\%\\] { 
            width: 70% !important; 
            max-width: 70% !important;
          }
          .w-\\[15\\%\\] { 
            width: 15% !important; 
            max-width: 15% !important;
          }
          .w-\[55\%\] {
            width: 55% !important;
            max-width: 55% !important;
          }

          .w-full { 
            width: 100% !important; 
          }

          .w-16 { 
            width: 16px !important; 
            max-width: 16px !important;
          }
          .h-16 { 
            height: 16px !important; 
            max-height: 16px !important;
          }
          
          /* Enhanced image styling for printing */
          img {
            width: 70px !important;
            height: 70px !important;
            max-width: 70px !important;
            max-height: 70px !important;
            display: block !important;
            margin: 0 auto !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .text-left { text-align: left; }
          
          .flex { display: flex; }
          .justify-between { justify-content: space-between; }
          .flex-1 { flex: 1; }
          .items-center { align-items: center; }
          
          .mb-1 { margin-bottom: 4px; }
          .mb-2 { margin-bottom: 8px; }
          .mb-3 { margin-bottom: 12px; }
          .my-2 { margin: 8px 0; }
          .py-1 { padding: 4px 0; }
          .pt-2 { padding-top: 8px; }
          .pr-2 { padding-right: 8px; }
          .ml-4 { margin-left: 16px; }
          
          .border-t { border-top: 1px solid #000; }
          .border-b { border-bottom: 1px solid #000; }
          .border-dashed { border-style: dashed; }
          .border-black { border-color: #000; }
          
          .leading-tight { line-height: 1.1; }
          .whitespace-nowrap { white-space: nowrap; }
          
          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            #kotDiv {
              width: 302px !important;
              margin: 0 !important;
              padding: 8px !important;
            }
            
            img {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            /* Ensure table structure is maintained in print */
            .w-\\[70\\%\\] { 
              width: 70% !important; 
              max-width: 70% !important;
            }
            .w-\\[15\\%\\] { 
              width: 15% !important; 
              max-width: 15% !important;
            }

            .w-\[55\%\] {
              width: 55% !important;
              max-width: 55% !important;
            }

          }
        </style>
      `;

      // Set up the listener BEFORE sending the event
      ipcRenderer.once('kot-print-success', async (event, { status, error }) => {
        if (status === 'success') {
          toast.success('KOT printed successfully!', { duration: 1000 });
        } else {
          console.error('KOT Print failed:', error);
          toast.error('KOT Print failed. Please check your printer settings.', {
            autoClose: 1000,
          });
          setLoading(false);
        }
      });

      // Send the HTML content for direct printing
      ipcRenderer.send('print-kot-html', {
        htmlContent: kotHTML,
        cssContent: printCSS,
        printer: sysInfo.pharmacyBillPriinter,
      });

    } catch (error) {
      console.error('Failed to prepare KOT for printing:', error);
      toast.error('Failed to prepare KOT for printing.', {
        autoClose: 1000,
      });
    }
  };

  const handlePrint = async () => {
    if(generalsettings?.phBillType === 'KOT'){
      printKOT();
    } else{
      if (normalBillPrintRef.current) {
        await normalBillPrintRef.current.handleDotMatrixPrint();
      }
    } 
    
  };

  return (
    <div className='flex h-full bg-background'>
      {/* <LeftMenu /> */}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      {loading && <Spinner />}

      <div className='flex-1 flex h-full flex-col overflow-auto'>
        {/* Header */}
        <div className="bg-white p-4 mb-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center">
                Pharmacy Sales Return
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex gap-2">
                {/* Patient Selection */}
                <div className="" style={{ width: '420px' }}>
                  <Select
                    placeholder="Select Patient..."
                    inputId="patient-input"
                    value={options.find(option => option.value === selectedPatient?.PatientID)}
                    options={options}
                    formatOptionLabel={customOptionLabel}
                    onInputChange={(value, { action }) => {
                      if (action === 'input-change') {
                        handleMobileChange(value);
                      }
                    }}
                    onChange={(selectedOption) => {
                      handleSuggestionClick(selectedOption);
                    }}
                    ref={mobileInputRef}
                    isClearable
                    filterOption={null} // Disable client-side filtering since we're doing server-side filtering
                    noOptionsMessage={() => {
                      if (loading) return "Loading...";
                      return "No matches found";
                    }}
                    styles={customSelectStyles}
                    className="w-full"
                    isLoading={loading} // Show loading state
                  />
                </div>
                {selectedPatient && (
                  <button
                    type="button"
                    className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all flex items-center gap-2"
                    title="View Patient History"
                    onClick={() => {
                      setShowPatientSidebar(true);
                    }}
                  >
                    <FiClock className="w-4 h-4" />
                    <span className="text-xs">View Bills</span>
                  </button>
                )}
                <div className="flex items-center gap-2">
                  {/* Toggle Button */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-300">
                    <button
                      type="button"
                      onClick={() => {
                        setSearchMode('invoice');
                        setInvoiceNumber('');
                        setOpNumber('');
                        invoiceNumberRef.current?.focus();
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        searchMode === 'invoice'
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                      title="Search by Invoice Number"
                    >
                      Invoice
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchMode('op');
                        setInvoiceNumber('');
                        setOpNumber('');
                        invoiceNumberRef.current?.focus();
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        searchMode === 'op'
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                      title="Search by OP Number"
                    >
                      OP No
                    </button>
                  </div>
                  {/* Input Field */}
                  <input
                    type="text"
                    ref={invoiceNumberRef}
                    className="w-48 p-1 pl-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition-colors font-bold"
                    value={searchMode === 'invoice' ? invoiceNumber : opNumber}
                    onChange={(e) => {
                      if (searchMode === 'invoice') {
                        setInvoiceNumber(e.target.value.toUpperCase());
                      } else {
                        setOpNumber(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleInvoiceSearch();
                      }
                    }}
                    onBlur={() => {
                      if (searchMode === 'op') {
                        handleOpnumBlur();
                      }
                    }}
                    placeholder={searchMode === 'invoice' ? 'Enter invoice number' : 'Enter OP number'}
                  />
                  <button
                    className="px-4 py-1 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    onClick={handleInvoiceSearch}
                  >
                    <FiSearch size={14} />
                    Search
                  </button>
                  <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPrev}
                      onChange={(e) => setIsPrev(e.target.checked)}
                      className="cursor-pointer"
                    />
                    is_prev
                  </label>
                </div>
                {invoiceData && (
                  <button
                    className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
                    onClick={resetForm}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white ml-3 mr-3 rounded-xl shadow-md p-3 mb-3 flex-1 flex flex-col min-h-0">
          {/* Content Area */}
          <div className="flex-1 flex flex-col min-h-0 space-y-4">
            {/* Invoice Details Card - Compact */}
            {invoiceData ? (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-200 flex-shrink-0">
                {/* First Row - Basic Info */}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div>
                      <h3 className="text-sm font-medium text-black">Invoice Number</h3>
                      <div className="text-xl font-bold text-indigo-600">{invoiceData.invoiceNumber}</div>
                    </div>
                    <div className="border-l border-indigo-300 pl-4">
                      <h3 className="text-sm font-medium text-black">Invoice Date</h3>
                      <div className="text-sm font-semibold text-gray-800">{invoiceData.invoiceDate}</div>
                    </div>
                    <div className="border-l border-indigo-300 pl-4">
                      <h3 className="text-sm font-medium text-black">Patient</h3>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-800">{invoiceData.patient.name}</span>
                        {invoiceData.patient.mobile && (
                          <span className="text-xs text-black">{invoiceData.patient.mobile}</span>
                        )}
                      </div>
                    </div>
                    <div className="border-l border-indigo-300 pl-4">
                      <h3 className="text-sm font-medium text-black">Doctor</h3>
                      <div className="text-sm font-semibold text-gray-800">{invoiceData.doctor || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Second Row - Financial Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 pt-3 border-t border-indigo-200 mb-3">
                  <div>
                    <h3 className="text-xs font-medium text-black mb-1">Total Amount</h3>
                    <div className="text-sm font-bold text-gray-800">₹{formatDecimal(invoiceData.totalAmount)}</div>
                  </div>
                  <div>
                    <h3 className="text-xs font-medium text-black mb-1">Discount Amount</h3>
                    <div className="text-sm font-semibold text-gray-800">₹{formatDecimal(invoiceData.discountAmount)}</div>
                  </div>
                  <div>
                    <h3 className="text-xs font-medium text-black mb-1">Subtotal</h3>
                    <div className="text-sm font-semibold text-gray-800">₹{formatDecimal(invoiceData.subtotal)}</div>
                  </div>
                  <div>
                    <h3 className="text-xs font-medium text-black mb-1">Total GST</h3>
                    <div className="text-sm font-semibold text-gray-800">₹{formatDecimal(invoiceData.totalTax)}</div>
                  </div>
                  <div>
                    <h3 className="text-xs font-medium text-black mb-1">SGST</h3>
                    <div className="text-sm font-semibold text-gray-800">₹{formatDecimal(invoiceData.totalSGST)}</div>
                  </div>
                  <div>
                    <h3 className="text-xs font-medium text-black mb-1">CGST</h3>
                    <div className="text-sm font-semibold text-gray-800">₹{formatDecimal(invoiceData.totalCGST)}</div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-black mb-1">Grand Total</h3>
                    <div className="text-xl font-bold text-indigo-600">₹{formatDecimal(invoiceData.grandTotal)}</div>
                  </div>
                </div>
                
                {/* Return Reason and Actions - Compact */}
                <div className="flex items-center gap-4 pt-3 border-t border-indigo-200">
                  <div className="flex-1 max-w-xs">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Return Reason <span className="text-red-500">*</span>
                    </label>
                    <Select
                      options={returnReasons}
                      value={returnReasons.find(r => r.value === returnReason)}
                      onChange={(selected) => setReturnReason(selected?.value || '')}
                      ref={returnReasonRef}
                      styles={customSelectStyles}
                      placeholder="Select reason"
                      className="w-full text-xs"
                    />
                  </div>
                  <div className="flex gap-2 mt-5">
                    <button
                      className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                      onClick={handleSubmitReturn}
                      ref={submitButtonRef}
                      disabled={isButtonDisabled}
                    >
                      Process Return
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors"
                      onClick={resetForm}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 border-2 border-dashed border-gray-300 p-8 rounded-xl text-center flex-shrink-0">
                <FiPackage className="mx-auto text-4xl text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-black mb-2">No Invoice Loaded</h3>
                <p className="text-sm text-gray-500">Enter an invoice number above to view details</p>
              </div>
            )}

            {/* Return Items Table */}
            {invoiceData ? (
              <div className="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col min-h-0">
                <div className="p-4 border-b border-gray-200 flex-shrink-0">
                  <h3 className="font-semibold text-gray-800">Return Items</h3>
                </div>

                <div className="flex-1 overflow-auto min-h-0">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">Medicine</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">Batch/Expiry</th>
                        <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-700 uppercase bg-gray-50">Qty</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">Return Qty</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">Rate</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">Return Amount</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">SGST</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">CGST</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-700 uppercase bg-gray-50">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {returnItems.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-1.5">
                            <div className="font-medium text-sm text-black">{item.name}</div>
                          </td>
                          <td className="px-3 py-1.5 text-xs text-black">
                            <div>{item.batchNo}</div>
                            <div className="text-xs">{item.expiryDate}</div>
                          </td>
                          <td className="px-3 py-1.5 text-xs text-center font-medium">{formatDecimal(item.qty)}</td>
                          <td className="px-3 py-1.5">
                            <input
                              type="number"
                              min="0"
                              max={item.availableQty}
                              className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-center text-sm font-medium"
                              value={item.returnQty || ''}
                              onChange={(e) => handleReturnQtyChange(index, e.target.value)}
                              onKeyDown={(e) => handleReturnQtyKeyDown(e, index)}
                              ref={(el) => (returnQtyRefs.current[index] = el)}
                              onFocus={(e) => e.target.select()}
                            />
                          </td>
                          <td className="px-3 py-1.5 text-xs text-left">₹{formatDecimal(item.rate)}</td>
                          <td className="px-3 py-1.5 text-xs text-left font-medium">
                            {item.returnAmount ? `₹${formatDecimal(item.returnAmount)}` : '-'}
                          </td>
                          <td className="px-3 py-1.5 text-xs text-left">
                            {item.returnSGST ? `₹${formatDecimal(item.returnSGST)}` : '-'}
                          </td>
                          <td className="px-3 py-1.5 text-xs text-left">
                            {item.returnCGST ? `₹${formatDecimal(item.returnCGST)}` : '-'}
                          </td>
                          <td className="px-3 py-1.5 text-xs text-left font-bold text-indigo-600">
                            {item.returnTotal ? `₹${formatDecimal(item.returnTotal)}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </div>
            ) : (
              <div className="bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex-shrink-0">
                <div className="p-8 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-black mb-2">No Return Items</h3>
                  <p className="text-sm text-gray-500">Load an invoice to view return items</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Return Summary Footer */}
        {invoiceData && (
          <div className="bg-white ml-3 mr-3 mb-2 rounded-lg shadow-sm border border-gray-300 flex-shrink-0">
            <div className="p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Invoice Number */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-black">Invoice:</span>
                  <span className="text-sm font-bold text-black">{invoiceData.invoiceNumber}</span>
                </div>

                {/* Return SGST */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-black">Return SGST:</span>
                  <span className="text-sm font-semibold text-black">₹{formatDecimal(calculateTotalReturnSGST())}</span>
                </div>

                {/* Return CGST */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-black">Return CGST:</span>
                  <span className="text-sm font-semibold text-black">₹{formatDecimal(calculateTotalReturnCGST())}</span>
                </div>


                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={printAfterSave} 
                      onChange={(e) => setPrintAfterSave(e.target.checked)}
                      className="text-text-black focus:ring-gray-500 h-4 w-4"
                    />
                    <span className="ml-1.5 text-xs text-black">Print on save</span>
                  </label>
                </div>

                {/* Total Return */}
                <div className="flex items-center gap-2 border-l border-r border-gray-300 px-4">
                  <span className="text-xs font-semibold text-black">Total Return:</span>
                  <span className="text-base font-bold text-red-600">₹{formatDecimal(calculateTotalReturn())}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Components - Hidden */}
      {generalsettings?.phBillTemplate === 'A5' ? (
        <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          <ReturnPrint
            ref={normalBillPrintRef}
            invoiceNumber={invoiceData?.invoiceNumber || ''}
            selectedPatient={{
              PatientID: header?.PatientID || 0,
              PatientName: invoiceData?.patient?.name || '',
              Age: invoiceData?.patient?.age || '',
              Gender: invoiceData?.patient?.gender || '',
              MobileNo: invoiceData?.patient?.mobile || '',
            }}
            selectedDoctor={{
              value: header?.RefDoctorID || 0,
              label: invoiceData?.doctor || '',
              DoctorName: invoiceData?.doctor || ''
            }}
            payingAmount={0}
            date={billedDate}
            products={returnItems.filter(item => item.returnQty > 0).map(item => ({
              id: item.id,
              name: item.name,
              batch: {
                batchNo: item.batchNo,
                expiryDate: item.expiryDate,
                mrp: item.mrp,
                gstRate: item.gstRate,
                availableQty: item.availableQty,
                purchasePrice: item.purchasePrice
              },
              qty: item.returnQty,
              rate: item.rate,
              amount: item.returnAmount.toFixed(2),
              discountAmount: item.returnDiscountAmount.toFixed(2),
              gstAmount: (parseFloat(item.returnSGST || 0) + parseFloat(item.returnCGST || 0)).toFixed(2),
              totalAmount: item.returnTotal.toFixed(2),
              discount: item.discount || ''
            }))}
            calculateTotal={calculateTotal}
            calculateGrandTotal={calculateGrandTotal}
            calculateTotalDiscount={calculateTotalDiscount}
            calculateTotalGST={calculateTotalGST}
            grand_total={parseFloat(calculateGrandTotal())}
            balance_due={parseFloat(calculateGrandTotal())}
            generalSetting={generalsettings}
            sysInfo={sysInfo}
            username={userInfo?.UserName}
            setTotalPages={setTotalPages}
            currentPage={currentPage}
          />
        </div>
      ) : (
        <div style={{ position: 'absolute', opacity: 0, top: '-10000px', left: '-10000px' }}>
          <KotReturnBillPrint
            invoiceNumber={invoiceData?.invoiceNumber || ''}
            selectedPatient={{
              PatientID: header?.PatientID || 0,
              PatientName: invoiceData?.patient?.name || '',
              Age: invoiceData?.patient?.age || '',
              Gender: invoiceData?.patient?.gender || '',
              MobileNo: invoiceData?.patient?.mobile || '',
            }}
            selectedDoctor={{
              value: header?.RefDoctorID || 0,
              label: invoiceData?.doctor || '',
              DoctorName: invoiceData?.doctor || ''
            }}
            payingAmount={0}
            date={billedDate}
            products={returnItems.filter(item => item.returnQty > 0).map(item => (
              {
              id: item.id,
              name: item.name,
              batch: {
                batchNo: item.batchNo,
                expiryDate: item.expiryDate,
                mrp: item.mrp,
                gstRate: item.gstRate,
                availableQty: item.availableQty,
                purchasePrice: item.purchasePrice
              },
              qty: item.returnQty,
              rate: item.rate,
              amount: item.returnAmount.toFixed(2),
              discountAmount: item.returnDiscountAmount.toFixed(2),
              gstAmount: (parseFloat(item.returnSGST || 0) + parseFloat(item.returnCGST || 0)).toFixed(2),
              totalAmount: item.returnTotal.toFixed(2),
              discount: item.discount || ''
            }))}
            calculateTotal={calculateTotal}
            calculateGrandTotal={calculateGrandTotal}
            calculateTotalDiscount={calculateTotalDiscount}
            calculateTotalGST={calculateTotalGST}
            grand_total={parseFloat(calculateGrandTotal())}
            balance_due={parseFloat(calculateGrandTotal())}
            generalSetting={generalsettings}
            sysInfo={sysInfo}
            username={userInfo?.UserName}
            setTotalPages={setTotalPages}
            currentPage={currentPage}
          />
        </div>
      )}

      {/* Patient History Sidebar */}
      {showPatientSidebar && selectedPatient && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 z-[100] transition-opacity"
            onClick={() => {
              setShowPatientSidebar(false);
              setExpandedBillId(null);
            }}
          />
          
          {/* Sidebar */}
          <div className={`fixed top-0 right-0 h-full w-[650px] bg-white shadow-2xl z-[101] transform transition-transform duration-300 ease-in-out ${
            showPatientSidebar ? 'translate-x-0' : 'translate-x-full'
          }`}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="bg-indigo-600 text-white p-4 flex items-center justify-between border-b border-indigo-700">
                <div>
                  <h2 className="text-lg font-bold">Patient Bills</h2>
                  <p className="text-sm text-indigo-100 mt-1">{selectedPatient.PatientName}</p>
                </div>
                <button
                  onClick={() => {
                    setShowPatientSidebar(false);
                    setExpandedBillId(null);
                  }}
                  className="p-2 hover:bg-indigo-700 rounded-lg transition-colors"
                  title="Close"
                >
                  <FiX size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                <div>
                  {patientHistory.length > 0 ? (
                    <div className="space-y-3">
                      {patientHistory.map((bill, index) => {
                        const isExpanded = expandedBillId === bill.InvoiceNo;
                        
                        return (
                          <div key={index} className="bg-white rounded-lg border border-gray-400 hover:border-gray-500 transition-colors overflow-hidden">
                            {/* Bill Header */}
                            <div className="p-3 flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-sm font-semibold text-black">Bill No: {bill.InvoiceNo}</p>
                                  {bill.DoctorName && (
                                    <span className="text-xs text-gray-600">• {bill.DoctorName}</span>
                                  )}
                                </div>
                                <p className="text-xs text-black">Bill Date: {bill.EntryDate ? bill.EntryDate.split('T')[0] : 'N/A'}</p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <span className="text-sm font-bold text-indigo-600">Grand Total : ₹{((bill.TotalAmount || 0) - (bill.DiscountAmount || 0)).toFixed(2)}</span>
                                <button
                                  onClick={() => {
                                    if (expandedBillId === bill.InvoiceNo) {
                                      setExpandedBillId(null);
                                    } else {
                                      setExpandedBillId(bill.InvoiceNo);
                                    }
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  title={isExpanded ? "Collapse" : "Expand"}
                                >
                                  {isExpanded ? (
                                    <FiChevronUp className="text-gray-600" size={18} />
                                  ) : (
                                    <FiChevronDown className="text-gray-600" size={18} />
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Bill Details - Expandable */}
                            {isExpanded && (
                              <div className="border-t border-gray-300 bg-gray-50 p-3">
                                <div className="space-y-2 mb-3">
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-600">Total Amount:</span>
                                    <span className="font-medium text-black">₹{parseFloat(bill.TotalAmount || 0).toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-600">Discount:</span>
                                    <span className="font-medium text-black">₹{parseFloat(bill.DiscountAmount || 0).toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-600">Tax Amount:</span>
                                    <span className="font-medium text-black">₹{parseFloat(bill.TaxAmount || 0).toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-xs font-bold border-t border-gray-300 pt-2">
                                    <span className="text-gray-800">Grand Total:</span>
                                    <span className="text-indigo-600">₹{((bill.TotalAmount || 0) - (bill.DiscountAmount || 0)).toFixed(2)}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleBillSelect(bill.InvoiceNo)}
                                  className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                                >
                                  <MdOutlineAssignmentReturn size={16} />
                                  Select for Return
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-black">No bills found for this patient</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PharmacySalesReturn;